import { useCallback } from 'react';
import {
  useAmplitudeContext,
  AmplitudeInstance,
} from '../components/AmplitudeProvider';
import type {
  EventType,
  EventProperties,
  EventOptions,
  EventWindowProperties,
} from '../types';
import {
  getIds as getUserIds,
  type Ids,
} from '../components/AmplitudeProvider/helpers';
import {
  getEventWindowProperties,
  replacePasswordValues,
  replaceKeyWordValues,
} from '../helpers';
import { APP_ENV } from '../config';

export interface LogEventProps {
  eventType: EventType;
  eventPropertiesIn?: EventProperties;
  eventOptions?: EventOptions;
}

export interface UseAmplitude {
  logEvent: (
    eventType: EventType,
    eventProperties?: EventProperties,
    eventOptions?: EventOptions,
  ) => void;
  logEventWithTimestamp: (
    eventType: EventType,
    eventProperties?: EventProperties,
    eventOptions?: EventOptions,
  ) => void;
  reset: () => void;
  sendEvents: () => void;
  getIds: () => Ids;
  setUserId: (userId: string) => void;
  getEventWindowProperties: () => EventWindowProperties;
  amplitude?: AmplitudeInstance;
  eventProperties?: object;
}

function useAmplitude(eventProperties: object = {}): UseAmplitude {
  const { amplitude, eventProperties: inheritedProperties } =
    useAmplitudeContext();

  const logEvent = useCallback(
    (
      eventType: EventType,
      eventPropertiesIn?: EventProperties,
      eventOptions?: EventOptions,
    ): void => {
      if (!amplitude) {
        if (APP_ENV === 'qa')
          console.log(
            'Amplitude not initialised or instance not found - unable to log event',
          );
        return;
      }
      const windowProperties = getEventWindowProperties();
      const properties = {
        ...inheritedProperties,
        ...eventProperties,
        ...eventPropertiesIn,
        ...windowProperties,
      };

      amplitude
        .track(
          eventType,
          replaceKeyWordValues(replacePasswordValues(properties)),
          eventOptions,
        )
        .promise.then(() => {
          console.log('Event sent to Amplitude');
        });
    },
    [amplitude, getEventWindowProperties],
  );

  const logEventWithTimestamp = useCallback(
    (
      eventType: EventType,
      eventPropertiesIn?: EventProperties,
      eventOptions?: EventOptions,
    ): void => {
      if (!amplitude) {
        if (APP_ENV === 'qa')
          console.log(
            'Amplitude not initialised or instance not found - unable to log event with timestamp',
          );
        return;
      }
      const windowProperties = getEventWindowProperties();
      const properties = {
        ...inheritedProperties,
        ...eventProperties,
        ...eventPropertiesIn,
        ...windowProperties,
      };
      const options = {
        ...eventOptions,
        time: Date.now(),
      };
      amplitude.track(
        eventType,
        replaceKeyWordValues(replacePasswordValues(properties)),
        options,
      );
    },
    [amplitude, getEventWindowProperties],
  );

  const sendEvents = useCallback((): void => {
    if (!amplitude) {
      if (APP_ENV === 'qa')
        console.log(
          'Amplitude not initialised or instance not found - unable to send events',
        );
      return;
    }
    amplitude.flush();
  }, [amplitude]);

  const reset = useCallback((): void => {
    if (!amplitude) {
      if (APP_ENV === 'qa')
        console.log(
          'Amplitude not initialised or instance not found - unable to reset amplitude',
        );
      return;
    }
    amplitude.reset();
  }, [amplitude]);

  const getIds = useCallback((): Ids => getUserIds(amplitude), [amplitude]);

  const setUserId = useCallback(
    (userId: string): void => {
      if (!amplitude) {
        if (APP_ENV === 'qa')
          console.log(
            'Amplitude not initialised or instance not found - unable to set user ID',
          );
        return;
      }
      amplitude.setUserId(userId);
    },
    [amplitude],
  );

  return {
    logEvent,
    logEventWithTimestamp,
    reset,
    sendEvents,
    getIds,
    setUserId,
    amplitude,
    getEventWindowProperties,
    eventProperties: inheritedProperties,
  };
}

export default useAmplitude;
